import { matchPath } from 'react-router-dom';
import {
  getRouteNavTitle,
  isCardsCatalogBrowseRoute,
  isMobileFullBleedBrowseRoute,
  isMobileFullBleedHeroRoute,
  routeMetadataFor,
} from './routeMetadata';

export { isCardsCatalogBrowseRoute, isMobileFullBleedBrowseRoute, isMobileFullBleedHeroRoute };

export type RouteChrome = {
  showMobileTabs: boolean;
  hideMobileHeader: boolean;
  hideFullWidthFooter: boolean;
  /**
   * Primary browse tabs should feel like an app shell on mobile — hide the legal
   * footer strip there only. Desktop keeps the full-width footer (Sets / Errata / etc.).
   */
  hideFullWidthFooterOnMobile: boolean;
  lockDesktopShellScroll: boolean;
  disableMobileRouteTransition: boolean;
  /**
   * Drop AppLayout horizontal padding on mobile so heroes and carousels can be
   * true full-bleed. Negative-margin breakout is clipped by MobileRouteTransition’s overflowX.
   */
  flushMobileHorizontalPadding: boolean;
};

const DEFAULT_ROUTE_CHROME: RouteChrome = {
  showMobileTabs: false,
  hideMobileHeader: false,
  hideFullWidthFooter: false,
  hideFullWidthFooterOnMobile: false,
  lockDesktopShellScroll: false,
  disableMobileRouteTransition: false,
  flushMobileHorizontalPadding: false,
};

export function isDeckEditorRoute(pathname: string): boolean {
  return pathname === '/decks/new' || Boolean(matchPath('/decks/:deckId/edit', pathname));
}

export function isCollectionScannerRoute(pathname: string): boolean {
  return pathname === '/collection/scan';
}

const PLAY_STATIC_SUBROUTES = new Set(['/play/join', '/play/match']);

export function isActiveMatchRoute(pathname: string): boolean {
  return Boolean(matchPath('/play/:sessionId', pathname)) && !PLAY_STATIC_SUBROUTES.has(pathname);
}

/** The table-side local counter — a focused, non-scrolling surface. */
export function isLocalMatchRoute(pathname: string): boolean {
  return pathname === '/play/match';
}

/**
 * Play, live match, and event management. These shells fill the phone viewport,
 * so a stuck double-tap zoom cannot be undone from inside them.
 */
export function shouldLockMobileViewportScale(pathname: string): boolean {
  return (
    pathname === '/play' ||
    pathname.startsWith('/play/') ||
    pathname === '/events' ||
    pathname.startsWith('/events/')
  );
}

/**
 * On mobile, large in-page titles stay in the page and crossfade into the app
 * navigation bar on scroll (HIG large title). Marketing home keeps its custom
 * hero and is the only opt-out.
 */
export function shouldCollapseMobilePageTitle(pathname: string): boolean {
  return pathname !== '/';
}

/**
 * Fallback mobile nav title from the route when a page has not registered one
 * yet (or uses a custom header). Matches bottom-tab / desktop section names.
 */
export function getMobileNavTitleFallback(pathname: string): string | undefined {
  return getRouteNavTitle(pathname);
}

export function routeShowsMobileTabs(pathname: string): boolean {
  if (isCollectionScannerRoute(pathname)) return false;
  if (isDeckEditorRoute(pathname)) return false;

  return routeMetadataFor(pathname)?.showMobileTabs === true;
}

export function getRouteChrome(pathname: string): RouteChrome {
  const isCardPrint = pathname === '/card-print';
  const isDeckEditor = isDeckEditorRoute(pathname);
  const isScanner = isCollectionScannerRoute(pathname);
  const isLocalMatch = isLocalMatchRoute(pathname);
  const isActiveMatch = isActiveMatchRoute(pathname);
  const isFocusedMobileRoute = isDeckEditor || isScanner || isLocalMatch || isActiveMatch;
  const routeMeta = routeMetadataFor(pathname);
  const hidesFullWidthFooter = isCardPrint || isFocusedMobileRoute;

  return {
    ...DEFAULT_ROUTE_CHROME,
    showMobileTabs: routeShowsMobileTabs(pathname),
    hideMobileHeader: isFocusedMobileRoute,
    hideFullWidthFooter: hidesFullWidthFooter,
    hideFullWidthFooterOnMobile: routeMeta?.hideFooterOnMobile === true,
    lockDesktopShellScroll: isCardPrint || isDeckEditor,
    disableMobileRouteTransition: isCardPrint || isScanner || isActiveMatch,
    flushMobileHorizontalPadding:
      isDeckEditor ||
      isLocalMatch ||
      isActiveMatch ||
      isMobileFullBleedHeroRoute(pathname) ||
      isMobileFullBleedBrowseRoute(pathname) ||
      pathname === '/matches',
  };
}
